import { Directive, Injector, OnDestroy, OnInit } from '@angular/core';
import { ILoadDataArgs } from '../../../../fi-items-list/fi-data-source-base.directive';
import { IItemsListDataFetchResult } from '../../../../fi-items-list/fi-items-list';
import { FiRemoteStateFullDataSource } from '@fi-shared/fi-items-list/fi-remote-state-full-data-source';
import { FileType, ObjectTypes } from '@fi-shared/types';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { FiChecklist2ReportType } from '@fi-shared/checklist/fi-checklist2-report/enums/fi-checklist2-report-type';
import {
    IChecklist2ReportAttachmentParameter,
    IChecklistV2ReportParameterFilter,
    ICheckpoint2AnswerAttachment,
} from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report';
import { FiChecklist2ReportService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report.service';
import { FiChecklist2ReportDataService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-data.service';
import { interval, Observable, Subject } from 'rxjs';
import { debounce, takeUntil } from 'rxjs/operators';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { FiChecklist2ReportDialogsService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-dialogs.service';
import { FiAttachmentsListItemComponent } from '@fi-shared/media-bank/fi-attachments/fi-attachment-list-item/fi-attachments-list-item.component';

@Directive()
export abstract class FiChecklist2AttachmentsBase
    extends FiRemoteStateFullDataSource<ICheckpoint2AnswerAttachment>
    implements OnInit, OnDestroy
{
    protected abstract reportType: FiChecklist2ReportType;
    protected abstract currentFilter: IChecklistV2ReportParameterFilter;
    protected abstract reportParameterId: number;
    protected abstract auditMode: boolean;
    protected abstract answersChanged$: Observable<void>;

    defaultSortColumnName: string = 'CreatedOn';
    defaultSortDirection: boolean = false;
    itemsListType: FiItemsListType.ChecklistsV2Attachments;
    objectType: ObjectTypes = ObjectTypes.MediaBank;

    protected fiChecklist2ReportService: FiChecklist2ReportService;
    protected fiChecklist2ReportDataService: FiChecklist2ReportDataService;
    protected fiChecklist2ReportDialogsService: FiChecklist2ReportDialogsService;

    numberOfRecordsToTake = 15;

    constructor(injector: Injector) {
        super(injector);
        this.setServices();
    }

    ngOnInit(): void {
        this.answersChanged$
            .pipe(
                takeUntil(this.ngOnDestroy$),
                debounce(() => interval(1000)),
            )
            .subscribe(() => {
                this.reloadData();
            });
    }

    loadListDataFn(loadArgs: ILoadDataArgs): Promise<IItemsListDataFetchResult<ICheckpoint2AnswerAttachment>> {
        const parameter = this.getItemsListParameter(loadArgs);
        const promise = this.fiChecklist2ReportService.getChecklistAttachmentsListData(parameter);
        return promise;
    }

    protected getItemsListParameter(loadArgs: ILoadDataArgs): IChecklist2ReportAttachmentParameter {
        const parameter = super.getItemsListParameter(loadArgs) as IChecklist2ReportAttachmentParameter;
        parameter.ReportType = this.reportType;
        parameter.Filter = this.currentFilter;
        parameter.AuditMode = this.auditMode;
        if (this.reportType === FiChecklist2ReportType.PastChecklists) {
            parameter.DateRange = this.fiChecklist2ReportDataService.getDateRange(this.reportParameterId);
        }
        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: false,
            FetchLightWeightObjects: false,
        };
        return parameter;
    }

    showChecklistAttachmentDetailsDialog(
        mediaBankFileReference: IMediaBankFileReference,
        attachmentsListItemComponent?: FiAttachmentsListItemComponent,
    ) {
        if (this.isPdfFile(mediaBankFileReference)) {
            attachmentsListItemComponent.openPdfFile();
            return;
        }

        // mediaBankFileReference.ObjectId = answer id
        this.fiChecklist2ReportService
            .getChecklistAnswerDetailsByAnswerIds([mediaBankFileReference.ObjectId], this.auditMode)
            .then((answerDetailsList) => {
                if (answerDetailsList.length === 1) {
                    this.fiChecklist2ReportDialogsService.showChecklistAttachmentDetailsDialog(
                        answerDetailsList[0],
                        mediaBankFileReference,
                    );
                } else {
                    throw new Error(
                        `getChecklistAnswerDetailsByAnswerIds wrong answer details, count ${answerDetailsList.length}, answer id ${mediaBankFileReference.ObjectId}`,
                    );
                }
            });
    }

    private isPdfFile(mediaBankFileReference: IMediaBankFileReference): boolean {
        return mediaBankFileReference.FileType === FileType.Pdf;
    }

    private setServices() {
        this.fiChecklist2ReportService = this.injector.get(FiChecklist2ReportService);
        this.fiChecklist2ReportDataService = this.injector.get(FiChecklist2ReportDataService);
        this.fiChecklist2ReportDialogsService = this.injector.get(FiChecklist2ReportDialogsService);
    }

    protected ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
